import { useEffect } from 'react';

import { useLoadTestResource } from './subHooks/useLoadTestResource';
import { useTestNavigator } from './subHooks/useTestNavigator';
import { useAudioActions } from './subHooks/useAudioActions';
import { animationModes } from '../../components/AvatarScene';
import { useAudioRecorder } from '../useAudioRecorder/useAudioRecorder';
import { useGetPartialRecording } from '../useGetParialRecording/useGetPartialRecording';
import {
    PartsMaxDuration,
    intentResponse,
    audioPlayReasons,
    audioEndReasons as promptEndReasons,
    colors,
} from '../../constants/constants';
import { uploadAudioFile } from '../../api/authApis';
import { formatDate } from '../../service/utils';
import { useIntentDetection } from './subHooks/useIntentDetection';
import { useTestProgression } from './subHooks/useTestProgression';
import { useInsperaIB } from './subHooks/useInsperaIB';
import { useStatesAndTimers } from './subHooks/useStatesAndTimers';
import { generateWordTimestamps, groupWordsToLines } from '../../service/TranscriptService';
import { isAppOpenedInIframe } from '../../screens/loginScreen/LoginScreen';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { pushFaroEvent } from '../../faroSession';

type promptEndReasonType = keyof typeof promptEndReasons;

export const useTestLogic = () => {
    const resource = useLoadTestResource();
    const navigator = useTestNavigator({
        promptList: resource.promptList,
        configuration: resource.configuration,
        endTestOnLastPrompt: () => {
            //additional logic to handle end if part 3 is not included
            const isPart3 = navigator.currentPrompt?.partName === 'PART3';

            if (resource.ratedContentFetched || !isPart3) {
                recorder.stopFullRecording();
            } else {
                pushFaroEvent('part3_rated_content_fetch_started');
                resource.getPart3RatedContent();
            }

            return isPart3 ? resource.ratedContentFetched : true;
        },
    });

    const { states, timeouts, intervals, refs, fns, data } = useStatesAndTimers();
    const inspera = useInsperaIB({ setUploadingTest: states.setUploadingTest });

    const appTheme = useSelector((state: RootState) => state.appConfigSlice.appTheme);
    if (inspera.insperaDetails.product === 'HOELT' && appTheme === 'black_on_white') {
        document.documentElement.style.setProperty('--secondary-ochre', colors.hoelt.primaryColor);
        document.documentElement.style.setProperty('--recording-bar-icon-color', colors.ielts.primaryColor);
    }
    const progression = useTestProgression({
        currentPrompt: navigator.currentPrompt,
        nextPrompt: navigator.nextPrompt,
    });
    const audio = useAudioActions({
        currentPrompt: navigator.currentPrompt,
        probeList: resource.probeList,
        currentPartRules: navigator.currentPartRules,
    });

    const recorder = useAudioRecorder({
        audioElementRef: audio.audioRef,
        onSpeechStart: () => {
            timeouts.speakingTimeout.clear();
            timeouts.noResponseTimeout.clear();
            states.setAnimationMode(animationModes.LISTENING);

            const intentAllowed = !navigator.currentPrompt?.noIntentDetection;
            const intentUsed = intentDetection.intentUsedRef.current;

            const useIntentDetection = !intentUsed && intentAllowed && hasTimeForIntent;

            if (useIntentDetection) intentDetection.startIntentRecording();
        },
        onSpeechEnd: () => {
            states.setAnimationMode(animationModes.IDLE);

            timeouts.speakingTimeout.start(() => {
                const intentAllowed = !navigator.currentPrompt?.noIntentDetection;
                const isIntentActive =
                    intentDetection.intentDetectionActiveRef.current || !intentDetection.intentUsedRef.current;

                const useIntentDetection = intentAllowed && isIntentActive;

                if (useIntentDetection) {
                    if (hasTimeForIntent) {
                        pushFaroEvent('intent_detection_initial_speech_ended');
                        intentDetection.initialSpeechEndedRef.current = true;
                    } else {
                        afterSpeechHandler();
                    }
                } else {
                    afterSpeechHandler();
                }
            }, fullResponse.seconds * 1000);
        },
        onRecordingEnd: async (blob) => {
            try {
                states.setUploadingTest(true);
                const responseUpload = await uploadAudioFile(
                    blob,
                    '.wav',
                    `${formatDate(new Date())}_fullrecording`,
                    resource.testTaker,
                    progression.insperaDetails,
                );
                await progression.uploadTestProgression(responseUpload.data.filePath);
                pushFaroEvent('full_recording_uploaded', { size: String(blob.size) });
            } catch (error) {
                console.error('Error uploading full recording:', error);
                states.setError('V302');
            } finally {
                inspera.sendMessageToIIB();
                if (!isAppOpenedInIframe()) {
                    states.setUploadingTest(false);
                }
            }
        },
    });

    const partialRecorder = useGetPartialRecording<promptEndReasonType>({
        recordedChunks: recorder.recordedChunks,
        onStart: () => {
            recorder.startVAD();
            runPromptTimers(!navigator.currentPrompt.showTimer && !navigator.currentPrompt.monologueQuestion);
            runNoResponseTimer();
        },
        onEnd: async (blob, promptEndReason) => {
            try {
                states.setPromptTimer(0);
                intervals.promptInterval.clear();
                timeouts.encourageTimeout.clear();
                timeouts.noResponseTimeout.clear();
                recorder.stopVAD();
                audio.resetRefs();

                const forceUploadProgression = refs.skipCurrentPartRef.current && !resource.ratedContentFetched;
                await progression.logPromptEndData(promptEndReason!, forceUploadProgression); // important to call before uploading audio

                navigator.moveToNextPrompt(refs.skipCurrentPartRef.current);

                const filename = `${navigator.currentPromptIdx + 1}_${formatDate(new Date())}`;
                const responseUpload = await uploadAudioFile(
                    blob,
                    '.wav',
                    filename,
                    resource.testTaker,
                    progression.insperaDetails,
                );

                pushFaroEvent('partial_recording_uploaded', { 
                    promptIdx: String(navigator.currentPromptIdx + 1),
                    size: String(blob.size)
                });

                progression.updateResponseFilePath(responseUpload.data.filePath);
                progression.uploadTestProgression();
            } catch (error) {
                console.error('Error uploading partial recording:', error);
                states.setError('V301');
            }
        },
    });

    const intentDetection = useIntentDetection({
        currentPrompt: navigator.currentPrompt,
        testEnded: navigator.testEnded,
        testTaker: resource.testTaker,
        recordedChunks: recorder.recordedChunks,
        onIntentReceived: (intentData) => {
            if (audio.isPlayingRef.current) {
                pushFaroEvent('intent_result_ignored', { reason: 'audio_playing' });
                return;
            }

            if (!hasTimeForIntent || refs.skipCurrentPartRef.current) {
                if (intentDetection.initialSpeechEndedRef.current) afterSpeechHandler('MOVE_TO_NEXT_PART');
                pushFaroEvent('intent_result_ignored', { reason: 'limited_time' });
                return;
            }

            const intentMap = {
                [intentResponse.REPEAT]: {
                    enabled: intentRepeat.enabled,
                    handler: () => repeatHandler('Intent Received as repeat'),
                },
                [intentResponse.MOVE_ON]: {
                    enabled: intentMoveOn.enabled,
                    handler: () => partialRecorder.stopPartialRecording('MOVE_ON_INTENT'),
                },
                [intentResponse.STUCK]: {
                    enabled: intentStuck.enabled,
                    handler: () => playVariantHandler('Intent Received as stuck'),
                },
            };

            const intentAction = intentMap[intentData.intent];

            if (intentAction) {
                if (intentAction.enabled) {
                    progression.updateIntentData(intentData);
                    intentAction.handler();
                } else {
                    afterSpeechHandler();
                }
            } else {
                //Fallback intent
                progression.updateIntentData(intentData);

                if (!recorder.isSpeakingRef.current) {
                    intentDetection.initialSpeechEndedRef.current
                        ? afterSpeechHandler()
                        : timeouts.speakingTimeout.start(afterSpeechHandler, 2000);
                }
            }
        },
    });

    const error = states.error ?? resource.error ?? progression.error;
    const hasTimeForIntent = navigator.currentPrompt?.partName === 'PART3' 
        ? ( navigator.currentPrompt.frame !== 'part3Intro') ? data.interrupTimer >= 20 : true
        : true;
    const testCanStart = resource.initialized && recorder.ready && !resource.loading && intentDetection.wsReady;
    const hideVisualizer = states.animationMode === animationModes.TALKING || !partialRecorder.isPartialActive;
    const { noResponse, fullResponse, partTimeOffset, intentRepeat, intentStuck, intentMoveOn } =
        navigator.currentPartRules;

    // Handler for when test taker finishes speaking
    const afterSpeechHandler = (reason?: promptEndReasonType) => {
        pushFaroEvent('speech_ended', { reason: reason || 'natural_end' });
        if (
            navigator.currentPrompt?.partName === 'PART3' &&
            refs.isVariantPlayedRef.current === false &&
            audio.encourageRef.current &&
            !refs.skipCurrentPartRef.current
        ) {
            return playBackupVariantHandler(audioPlayReasons.NOT_MUCH_RESPONDED);
        }
        if (audio.encourageRef.current && !refs.skipCurrentPartRef.current) return encourageHandler();

        partialRecorder.stopPartialRecording(reason ?? 'FULL_RESPONSE');
    };

    // General interrupt function used for part timer and prompt max duration timeout
    const interruptHandler = (reason: string) => {
        audio.interrupt({
            onPlay: (audioFileUrl) => {
                pushFaroEvent('interrupt_probe_playing', { reason });
                timeouts.noResponseTimeout.clear();
                timeouts.speakingTimeout.clear();
                recorder.stopVAD(); // this needs to be called immediately
                states.setAnimationMode(animationModes.TALKING);
                progression.logProgressionData(audioFileUrl, reason);
            },
            onEnded: () => {
                const endReason =
                    reason === audioPlayReasons.MAX_DURATION_FOR_PART_REACHED ? 'TIME_LIMIT_PART' : 'TIME_LIMIT_PROMPT';
                partialRecorder.stopPartialRecording(endReason);
            },
        });
    };

    // Handler when prompt max duration is reached
    const runPromptTimers = (ignore?: boolean) => {
        if (ignore) return;

        // Max prompt timer
        states.setPromptTimer(navigator.currentPrompt?.expectedMaxDuration ?? 0);

        intervals.promptInterval.start(() => {
            states.setPromptTimer((prev) => {
                if (prev <= 0) {
                    intervals.promptInterval.clear();
                    interruptHandler(audioPlayReasons.MAX_DURATION_REACHED);
                    return 0;
                }
                console.log('PROMPT TIMER: ', prev - 1);
                return prev - 1;
            });
        }, 1000);

        // Min prompt timer
        if (navigator.currentPrompt?.expectedMinDuration) {
            timeouts.encourageTimeout.start(() => {
                audio.encourageRef.current = false;
            }, navigator.currentPrompt.expectedMinDuration * 1000);
        }
    };

    // Handler for when test taker does not respond
    const runNoResponseTimer = () =>
        timeouts.noResponseTimeout.start(() => {
            pushFaroEvent('no_response_timeout_triggered');
            if (refs.skipCurrentPartRef.current) {
                pushFaroEvent('no_response_ignored', { reason: 'part_time_limited' });
                partialRecorder.stopPartialRecording('MOVE_TO_NEXT_PART');
            } else {
                noResponse.playVariant
                    ? playVariantHandler(audioPlayReasons.NO_RESPONSE)
                    : repeatHandler(audioPlayReasons.NO_RESPONSE);
            }
        }, noResponse.seconds * 1000);

    // Handler for repeating the current prompt
    const repeatHandler = (reason: string) => {
        audio.repeat({
            onPlay: (audioFileUrl) => {
                pushFaroEvent('prompt_repeat_started', { reason });
                recorder.stopVAD();
                timeouts.speakingTimeout.clear();
                states.setAnimationMode(animationModes.TALKING);
                progression.logProgressionData(audioFileUrl, reason);
            },
            onRepeat: (audioFileUrl) => {
                progression.logProgressionData(audioFileUrl, reason);
            },
            onEnded: () => {
                states.setAnimationMode(animationModes.IDLE);
                recorder.startVAD();
                runNoResponseTimer();
                if (navigator.currentPrompt?.partName === 'PART3' && !navigator.currentPrompt.noIntentDetection) {
                    intentDetection.resetIntentDetection();
                }
            },
            onMaxRepeat: () => {
                if (!navigator.currentPrompt.monologueQuestion) {
                    if(navigator.currentPrompt?.partName==='PART3'){
                        playVariantHandler('TT requested for 2nd repeat');
                    }else{
                        pushFaroEvent('max_repeat_reached', { part: navigator.currentPrompt?.partName });
                        partialRecorder.stopPartialRecording('REPEAT_STOP');
                    }
                }
            },
        });
    };

    // Handler for when test taker does not meet the minimum duration when answering
    const encourageHandler = () =>
        audio.encourage({
            onPlay: (audioFileUrl) => {
                pushFaroEvent('encourage_probe_playing');
                recorder.stopVAD();
                timeouts.speakingTimeout.clear();
                states.setAnimationMode(animationModes.TALKING);
                progression.logProgressionData(audioFileUrl, audioPlayReasons.NOT_MUCH_RESPONDED);
            },
            onEnded: () => {
                recorder.startVAD();
                states.setAnimationMode(animationModes.IDLE);
                if (navigator.currentPrompt?.partName === 'PART3' && !navigator.currentPrompt.noIntentDetection) {
                    intentDetection.resetIntentDetection();
                }
            },
            onMaxEncourage: () => {
                pushFaroEvent('max_encourage_reached', { part: navigator.currentPrompt?.partName });
                partialRecorder.stopPartialRecording('SHORT_RESPONSE');
            },
        });

    // Handler for playing a backup variant of the current prompt
    const playBackupVariantHandler = (reason: string) =>
        audio.playBackupVariant({
            onPlayVariant: (audioFileUrl) => {
                pushFaroEvent('backup_variant_playing', { reason });
                recorder.stopVAD();
                timeouts.speakingTimeout.clear();
                states.setAnimationMode(animationModes.TALKING);

                refs.isVariantPlayedRef.current = true;
                progression.logProgressionData(audioFileUrl, reason);
            },
            onEnded: () => {
                states.setAnimationMode(animationModes.IDLE);
                recorder.startVAD();
                runNoResponseTimer();
                if (navigator.currentPrompt?.partName === 'PART3' && !navigator.currentPrompt.noIntentDetection) {
                    intentDetection.resetIntentDetection();
                }
            },
            onMaxVariantsPlayed: () => {
                pushFaroEvent('max_backup_variants_reached', { part: navigator.currentPrompt?.partName });
                partialRecorder.stopPartialRecording('NO_RESPONSE');
            },
        });

    // Handler for playing a variant of the current prompt
    const playVariantHandler = (reason: string) =>
        audio.playVariant({
            onPlay: (audioFileUrl) => {
                pushFaroEvent('variant_playing', { reason });
                recorder.stopVAD();
                timeouts.speakingTimeout.clear();
                states.setAnimationMode(animationModes.TALKING);
                progression.logProgressionData(audioFileUrl, reason);
            },
            onPlayVariant: (audioFileUrl) => {
                refs.isVariantPlayedRef.current = true;
                progression.logProgressionData(audioFileUrl, reason);
            },
            onEnded: () => {
                states.setAnimationMode(animationModes.IDLE);
                recorder.startVAD();
                runNoResponseTimer();
                if (navigator.currentPrompt?.partName === 'PART3' && !navigator.currentPrompt.noIntentDetection) {
                    intentDetection.resetIntentDetection();
                }
            },
            onMaxVariantsPlayed: () => {
                pushFaroEvent('max_variants_reached', { part: navigator.currentPrompt?.partName });
                partialRecorder.stopPartialRecording('NO_RESPONSE');
            },
        });

    // Start test and recording
    useEffect(() => {
        if (progression.testStartTime) {
            navigator.startTest();
        } else if (testCanStart && !states.isAnimationLoading) {
            recorder.startFullRecording();
            progression.markTestStartTime();
        }
    }, [testCanStart, states.isAnimationLoading, progression.testStartTime]);

    // Handles the continuity of the test
    useEffect(() => {
        if (!navigator.currentPrompt) return;
        if (navigator.currentPrompt.playAudioTwice) {
            let number = parseInt(navigator.currentPrompt?.localizedMedia?.audioDuration || '0');
            let wordTimeStamp = generateWordTimestamps(navigator.currentPrompt.markDownText || '', number + 1);
            refs.subTitleRef.current = groupWordsToLines(wordTimeStamp);
            audio.audioRef.current.ontimeupdate = () => {
                if (navigator.currentPrompt.playAudioTwice && !navigator.currentPrompt.audioBarPause) {
                    const currentTime = audio.audioRef.current.currentTime;
                    let transcriptList = refs.subTitleRef.current.filter(
                        (cue: any) => cue.start <= currentTime && cue.end >= currentTime,
                    );
                    let currentPhrase = transcriptList.length > 0 ? transcriptList[0].word : '';
                    states.setCurrentSubTitle(currentPhrase);
                }
            };
        }
        audio.playPrompt({
            onPlay: (audioFileUrl) => {
                pushFaroEvent('prompt_playing', { 
                    promptIdx: String(navigator.currentPromptIdx),
                    part: navigator.currentPrompt.partName
                });
                refs.isVariantPlayedRef.current = false;
                progression.logProgressionData(audioFileUrl, audioPlayReasons.QUESTION_COMPLETED, true);
                states.setAnimationMode(animationModes.TALKING);

                runPromptTimers(!!navigator.currentPrompt.showTimer || !!navigator.currentPrompt.monologueQuestion);
            },
            onEnded: () => {
                const { expectedMaxDuration, showTimer, expectedMinDuration, monologueQuestion } =
                    navigator.currentPrompt;

                if (showTimer) {
                    states.setPrepTime(expectedMaxDuration);
                    states.setAnimationMode(animationModes.IDLE);
                    return;
                }
                if (monologueQuestion) states.setMonologueTime(expectedMaxDuration);

                if (expectedMaxDuration) {
                    if (expectedMinDuration) audio.encourageRef.current = true;

                    states.setAnimationMode(animationModes.IDLE);
                    partialRecorder.startPartialRecording();
                } else {
                    if (navigator.currentPrompt.playAudioTwice) {
                        navigator.currentPrompt.audioBarPause = true;
                        playPromptWithBeep();
                    } else {
                        progression.uploadTestProgression();
                        navigator.moveToNextPrompt(refs.skipCurrentPartRef.current);
                    }
                }
            },
        });
    }, [navigator.currentPrompt]);

    // Handle preparation time countdown
    useEffect(() => {
        if (!navigator.currentPrompt?.showTimer || states.prepTime < navigator.currentPrompt.expectedMaxDuration)
            return;

        intervals.prepTimeInterval.start(() => {
            states.setPrepTime((prev) => {
                if (prev <= 1) {
                    intervals.prepTimeInterval.clear();
                    navigator.moveToNextPrompt(refs.skipCurrentPartRef.current);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    }, [navigator.currentPrompt, states.prepTime]);

    // Handle Card Text and monologue prompt cleanup
    useEffect(() => {
        if (!navigator.currentPrompt) return;

        const { markDownText } = navigator.currentPrompt;
        const { type } = navigator.currentPrompt;
        if (type === 'rubric') states.setEnableHoeltPart2(true);

        if (markDownText) states.setCardText(markDownText);

        return () => {
            if (navigator.currentPrompt.monologueQuestion) {
                states.setCardText(undefined);
                states.setMonologueTime(0);
                states.setEnableHoeltPart2(false);
            }
        };
    }, [navigator.currentPrompt]);

    // Handle part timer
    useEffect(() => {
        if (!navigator.currentPrompt) return;

        if (navigator.testEnded) return intervals.partInterval.clear();

        const partDuration =
            PartsMaxDuration[navigator.currentPrompt.partName.toLowerCase() as keyof typeof PartsMaxDuration];
        states.setPartTimer(partDuration);

        intervals.partInterval.start(() => {
            states.setPartTimer((prev) => {
                if (prev <= partTimeOffset.seconds) {
                    refs.skipCurrentPartRef.current = true;
                }

                if (prev <= 0) {
                    intervals.partInterval.clear();
                    interruptHandler(audioPlayReasons.MAX_DURATION_FOR_PART_REACHED);
                    return 0;
                }

                console.log(`Time left for ${navigator.currentPrompt.partName}: ${prev - 1}`);
                return prev - 1;
            });
        }, 1000);

        return () => {
            intervals.partInterval.clear();
            refs.skipCurrentPartRef.current = false;
        };
    }, [navigator.currentPrompt?.partName, navigator.testEnded]);

    useEffect(() => {
        if (error && testCanStart) {
            audio.pause();
            fns.pause();
        }
    }, [error, testCanStart]);

    const playPromptWithBeep = () => {
        pushFaroEvent('prompt_with_beep_started');
        setTimeout(() => {
            audio.playBeep({
                onPlay: () => {
                    pushFaroEvent('beep_sound_playing');
                },
                onEnded: () => {
                    setTimeout(() => {
                        navigator.currentPrompt.audioBarPause = false;
                        audio.playPrompt({
                            onPlay: (audioFileUrl) => {
                                pushFaroEvent('prompt_playing_after_beep', { 
                                    promptIdx: String(navigator.currentPromptIdx),
                                    part: navigator.currentPrompt.partName
                                });
                                progression.logProgressionData(audioFileUrl, audioPlayReasons.QUESTION_COMPLETED, true);
                                states.setAnimationMode(animationModes.TALKING);
                            },
                            onEnded: () => {
                                progression.uploadTestProgression();
                                navigator.moveToNextPrompt(refs.skipCurrentPartRef.current);
                            },
                        });
                    }, 1500);
                },
            });
        }, 1500);
    };

    return {
        //status
        loaded: testCanStart,
        testEnded: navigator.testEnded,
        hideVisualizer,
        uploadingTest: isAppOpenedInIframe()
            ? states.uploadingTest && !inspera.awaitingAcknowledgement
            : states.uploadingTest,
        isFailedInsperaTestClosure: inspera.isFailedInsperaTestClosure,

        //data
        animationMode: states.animationMode,
        cardText: states.cardText,
        monologueTime: states.monologueTime,
        prepTime: states.prepTime,
        partTimer: states.partTimer,
        promptTimer: states.promptTimer,
        currentSubTitle: states.currentSubTitle,
        insperaDetails: inspera.insperaDetails,
        error,
        currentPrompt: navigator.currentPrompt,
        enableHoeltPart2: states.enableHoeltPart2,

        //funcs,
        setIsAnimationLoading: states.setIsAnimationLoading,

        //refs
        recordedChunks: recorder.recordedChunks,
        appTheme,
    };
};
